import axios from 'axios';

export type DeliveryEstimatesErrorType =
  | 'invalid-params'
  | 'unauthorized'
  | 'not-found'
  | 'conflict'
  | 'unavailable'
  | 'unknown';

export class DeliveryEstimatesError extends Error {
  public readonly type: DeliveryEstimatesErrorType;
  public readonly status?: number;
  public readonly cause: unknown;
  // Set only when the response body carried a message. `message` is never empty — it falls back to
  // the transport error — so it cannot be used to tell "the server explained itself" apart from
  // "something failed before the server answered".
  public readonly serverMessage?: string;

  constructor(
    type: DeliveryEstimatesErrorType,
    message: string,
    status: number | undefined,
    cause: unknown,
    serverMessage?: string,
  ) {
    super(message);
    this.name = 'DeliveryEstimatesError';
    this.type = type;
    this.status = status;
    this.cause = cause;
    this.serverMessage = serverMessage;
  }
}

export function toDeliveryEstimatesError(error: unknown): DeliveryEstimatesError {
  if (!axios.isAxiosError(error)) {
    return new DeliveryEstimatesError(
      'unknown',
      error instanceof Error ? error.message : 'Unknown error',
      undefined,
      error,
    );
  }

  const responseData: unknown = error.response?.data;
  const status = error.response?.status;
  const serverMessage =
    typeof responseData === 'object' && responseData !== null && 'message' in responseData
      ? (responseData as { message?: unknown }).message
      : undefined;
  const bodyMessage = typeof serverMessage === 'string' ? serverMessage : undefined;
  const message = bodyMessage ?? error.message;

  switch (status) {
    case 400:
      return new DeliveryEstimatesError('invalid-params', message, status, error, bodyMessage);
    case 401:
    case 403:
      return new DeliveryEstimatesError('unauthorized', message, status, error, bodyMessage);
    case 404:
      return new DeliveryEstimatesError('not-found', message, status, error, bodyMessage);
    case 409:
      return new DeliveryEstimatesError('conflict', message, status, error, bodyMessage);
    case 503:
      return new DeliveryEstimatesError('unavailable', message, status, error, bodyMessage);
    default:
      return new DeliveryEstimatesError('unknown', message, status, error, bodyMessage);
  }
}
